<template>
  <div>
    <SimpleWarningDialog
      v-if="showMeetingCompletingDialog"
      :title="$t('meetingCompleted')"
      :text="$t('meetingCompletedText')"
      @onPrimary="completeMeeting"
      :secondaryBtn="true"
      @onSecondary="showMeetingCompletingDialog = false"
    />
    <SimpleWarningDialog
      v-if="showMeetingCancellingDialog"
      :title="$t('meetingCancelled')"
      :text="$t('meetingCancelledText')"
      @onPrimary="cancelMeeting"
      :secondaryBtn="true"
      @onSecondary="showMeetingCancellingDialog = false"
    />
    <SimpleWarningDialog
      v-if="showDeletingAllScreenshotsDialog"
      :title="$t('deletingAllScreenshotsDialogTitle')"
      :text="$t('deletingAllScreenshotsDialogText')"
      @onPrimary="deleteAllScreenshots"
      :secondaryBtn="true"
      @onSecondary="showDeletingAllScreenshotsDialog = false"
    />
    <SimpleWarningDialog
      v-if="showOnlyParticipantError"
      :title="$t('onlyParticipantWarning')"
      @onPrimary="hideOnlyParticipantError"
    />
    <SimpleWarningDialog
      v-if="showCustomerLeftWarning"
      :title="$t('customerLeftTitle')"
      :text="$t('customerLeftText')"
      @onPrimary="onCloseCustomerLeftWarning"
    />
    <ProvisionsNotMetDialog
      v-if="showProvisionsNotMetDialog"
      :provisions="provisions"
      @onPrimary="hideProvisionsNotMetDialog"
    />

    <v-snackbar top color="success" v-model="sendSmsSnackbar" :timeout="3000">
      <p style="text-algin: center; font-size: 18px">{{ $t("smsSent") }}</p>
    </v-snackbar>
    <v-skeleton-loader v-if="isLoading" type="image,image"></v-skeleton-loader>
    <div
      id="jitsi-container"
      ref="jitsiContainer"
      style="height: 100%; width: 100%; height: 400px"
      :class="{ 'd-none': isLoading }"
    >
      <div class="actions-wrapper" v-if="showCustomActions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              :color="everyScreenshotChecked ? 'success' : ''"
              :disabled="!anyScreenshotCaptured"
              @click="selectAllScreenshots"
            >
              <v-icon dark>mdi-check-all</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("checkAll") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              :disabled="!anyScreenshotCaptured"
              @click="onClickDeleteAllScreenshots"
            >
              <v-icon dark>mdi-delete-sweep</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("deleteAll") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              @click="onClickToggleCustomerCard"
            >
              <v-icon>mdi-card-account-details</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("showCustomerCard") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              :disabled="imagesUploaded"
              @click="onClickCaptureScreenshot"
            >
              <v-icon>mdi-monitor-screenshot</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("captureScreenshot") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
            :disabled="!isVideoOtpEnabled"
              v-bind="attrs"
              v-on="on"
              icon
              @click="onClickSendSmsActivationCode"
            >
              <v-icon :color="phoneApproved ? 'success' : ''"
                >mdi-cellphone-message</v-icon
              >
            </v-btn>
          </template>
          <span>{{ $t("sendSmsActivationCode") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="isContractEnabled" 
              v-bind="attrs"
              v-on="on"
              icon
              :disabled="!imagesUploaded && isContractEnabled"
              @click="onClickToContractManagement"
            >
              <v-icon>mdi-file-document-edit</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("processToContractManagement") }}</span>
        </v-tooltip>

      </div>
    </div>
    <div class="buttons-wrapper" v-if="showCustomActions">
      <div class="alert-wrapper">
        <v-alert
          class="alert"
          :type="phoneApproved ? 'success' : 'error'"
          :color="phoneApproved ? 'green lighten-2' : 'red lighten-2'"
          >{{
            phoneApproved ? $t("phoneApproved") : $t("phoneNotApproved")
          }}</v-alert
        >
        <v-alert
          class="alert"
          :type="contractAlert ? 'success' : 'error'"
          :color="contractAlert ? 'green lighten-2' : 'red lighten-2'"
          >{{
            contractAlert ? $t("contractApproved") : $t("contractNotApproved")
          }}</v-alert
        >
        <v-alert
          v-if="isProcessAvailableForFaceRecognition"
          class="alert"
          :type="faceRecStatus === 2 ? 'success' : 'error'"
          :color="faceRecStatus === 2 ? 'green lighten-2' : 'red lighten-2'"
        >
          {{ faceRecStatus === 2 ? $t("faceApproved") : $t("faceNotApproved") }}
        </v-alert>
        <v-alert
          class="alert"
          :type="imagesUploaded ? 'success' : 'error'"
          :color="imagesUploaded ? 'green lighten-2' : 'red lighten-2'"
          >{{
            imagesUploaded ? $t("imagesApproved") : $t("imagesNotApproved")
          }}</v-alert
        >
      </div>
      <v-btn
        :dark="true"
        class="mr-4"
        @click="onClickCancelMeeting"
        :loading="cancelLoading"
      >
        <v-icon class="mr-2" color="white">mdi-phone-cancel</v-icon>
        <span class="color--primary--white">{{ $t("cancelMeeting") }}</span>
      </v-btn>
      <template v-if="imagesUploaded"> 
        <template v-if="(contractApproved || !isContractEnabled) && isCheckListCompleted"> 
          <v-btn
            class="mr-4"
            :color="'primary'"
            @click="onClickCompleteMeeting"
            :loading="completeLoading"
          >
            <v-icon class="mr-2" color="white">mdi-phone-check</v-icon>
            {{ $t("completeMeeting") }}
          </v-btn>
        </template>
        <template v-else>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-btn
                  class="mr-4"
                  :color="'primary'"
                  :disabled="true"
                  :loading="completeLoading"
                >
                  <v-icon class="mr-2" color="white">mdi-phone-check</v-icon>
                  {{ $t("completeMeeting") }}
                </v-btn>
              </div>
            </template>
            <span>{{ $t("provideContractManagementWarning") }}</span>
          </v-tooltip>
        </template>
      </template>
      <template v-else>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-btn
                class="mr-4"
                :color="'primary'"
                :disabled="true"
                :loading="completeLoading"
              >
                <v-icon class="mr-2" color="white">mdi-phone-check</v-icon>
                {{ $t("completeMeeting") }}
              </v-btn>
            </div>
          </template>
          <span>{{ $t("uploadYourScreenshots") }}</span>
        </v-tooltip>
      </template>

      <v-btn
        v-if="!imagesUploaded"
        :color="provisionsMet ? 'primary' : 'red'"
        :disabled="imagesUploading || !provisionsRetrieved"
        :loading="screenshotLoading"
        @click="onClickUploadScreenshots"
      >
        <v-icon class="mr-2" color="white">{{
          provisionsMet ? "mdi-cloud-upload" : "mdi-alert-circle"
        }}</v-icon>
        {{ $t("uploadScreenshots") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import JitsiMeeting from "./mixin/JitsiMeeting";

export default {
  mixins: [JitsiMeeting]
};
</script>
<style lang="scss">
#jitsiConferenceFrame0 {
  height: 400px;
  margin-right: 40px;
}
#jitsi-container {
  position: relative;
}
.actions-wrapper {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0px;
  right: 0;
  background-color: white;
  padding: 10px;
  align-items: center;
  transform: translateX(100%);
  button {
    margin-right: 10px;
  }
}
.alert-wrapper {
  display: flex;
  flex-wrap: wrap;
  width: 80%;
  .alert {
    margin-right: 5px;
    margin-bottom: 1px !important;
    padding: 1%;
    i.v-icon {
      margin-right: 0px !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/JitsiMeeting.vue